import { types, getEnv, SnapshotOut, Instance } from 'mobx-state-tree';
import { observable } from 'mobx';

import { BaseEventModel, createOddModel, gameStore } from './event';
import { IOdds, IGame, IDetail } from './../api/proto/apiContract';
import { ActionType, EventsItem, MatchTrackerType, ResultFlag } from './../dtos/apiLiveEventsDtos';
import { LiveStreamOperatorType, LiveStreamInApi } from './liveStream';
import { translation } from 'easit--localization';
import { ICommonAppEnvWithSBBaseApi, getSBApiStringToDateConverter, ITicketRateSyncService } from './index';
import { secondsToMinutesForMatchTime } from 'easit--utils';
import { IRenameTipData, getCompetitorNamesFromEventName, getRenameTipDataProviderFromCompetitors, IRenameTipDataProvider, IOddsModelCreator } from '../utils/eventsUtils';
import { MatchTrackerInApi } from './matchTracker';
import { OddModel } from './odd';

export const EventResultStore = types.model({
    //description: types.string, is it necessary? not referenced, uof provides null and causes error
    flags: types.number, // flags of ResultFlag, see IResult
    id: types.number,
    iconId: types.number,
    importantResult: types.boolean,
    mainResult: types.boolean,
    name: types.string,
    score1: types.string,
    score2: types.string
}).views(self => {

    return {

        get isSuspendedPlayer(): boolean {
            return (self.flags & ResultFlag.SuspendedPlayer) === ResultFlag.SuspendedPlayer;
        },

        get isGame(): boolean {
            return (self.flags & ResultFlag.Game) === ResultFlag.Game;
        },

        get isHalf(): boolean {
            return (self.flags & ResultFlag.HalfTime) === ResultFlag.HalfTime;
        },

        get isCurrentPeriod(): boolean {
            return (self.flags & ResultFlag.CurrentPeriod) === ResultFlag.CurrentPeriod;
        },

        get isBaseballBase(): boolean {
            return (self.flags & ResultFlag.BaseballBase) === ResultFlag.BaseballBase;
        }
    }
})
    .actions(self => {

        const tailorScoreValues = (): void => {
            tailorResultScoreValues(self, self.flags);
        }

        return {
            tailorScoreValues
        }
    });

export type IEventResultStore = Instance<typeof EventResultStore>;

export const tailorResultScoreValues = (data: { score1: string, score2: string }, flags: ResultFlag): void => {
    if ((flags & ResultFlag.Game) === ResultFlag.Game) {
        if (data.score1 === "50") {
            data.score1 = "A";
        }
        if (data.score2 === "50") {
            data.score2 = "A";
        }
    }
}

export const EventActionStore = types.model({
    id: types.string,
    imageMapCode: types.string,
    matchTime: types.maybeNull(types.number),
    name: types.string,
    sequenceId: types.number
});


export const LiveEventCompetitor = types.model({
    isServer: types.boolean,
    name: types.string
});

export const BaseLiveEventStore = types.compose(
    BaseEventModel,
    types.model({
        sportCategoryId: types.maybeNull(types.number),
        catIds: types.maybeNull(types.array(types.number)),
        results: types.optional(types.array(EventResultStore), []), //it may be empty for e.g. Formula 1, see view hasResults
        stateTime: types.maybeNull(types.number),  // seconds
        eventActions: types.optional(types.array(EventActionStore), []),
        stateName: types.optional(types.string, ''),
        competitors: types.optional(types.array(LiveEventCompetitor), []),
        liveStream: types.maybeNull(LiveStreamInApi),
        matchTracker: types.maybeNull(MatchTrackerInApi)
    })
)
    .views(self => {

        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        return {
            get mainResult(): IEventResultStore | null { //there may be not result for e.g. Formula 1
                return self.results.find(result => result.mainResult);
            },
            get importantResults() {
                return self.results ? self.results.filter(result => result.importantResult) : [];
            },
            get otherResults() {
                return self.results.filter(result => !result.importantResult && !result.mainResult);
            },
            get resultsOfPeriods() {
                return self.results.filter(result => ((result.flags & ResultFlag.IsPeriod) === ResultFlag.IsPeriod) || ((result.flags & ResultFlag.Game) === ResultFlag.Game));
            },
            get hasResults(): boolean {
                return self.results.length > 0;
            },
            get eventTime(): number | undefined {
                return self.stateTime == null ? undefined : secondsToMinutesForMatchTime(self.stateTime);
            },
            get sRNotificationHandler() {
                return env.sRNotificationHandler;
            }
        }
    })
    .actions(self => {

        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        const apiStringToDateConverter = getSBApiStringToDateConverter(env);

        let parentSetBaseLiveData = self.setBaseLiveData;
        const setBaseLiveData = (event: EventsItem, renameTipDataProvider: IRenameTipDataProvider, createOddModel: IOddsModelCreator) => {
            parentSetBaseLiveData(event, renameTipDataProvider, createOddModel);
            self.startDate = apiStringToDateConverter(event.Date);
        }

        const eventHasMatchTracker = (event: EventsItem): boolean => {
            return (event.MatchTracker || event.IsMatchTrackerAvailable) && (
                env.settings.matchTrackerUA === -2
                || env.settings.matchTrackerUA === 0 // matchtracker also for not logged
                || (env.loggedUserId > 0 && env.loggedUserId === env.settings.matchTrackerUA));
        }

        const eventHasLiveStream = (event: EventsItem): boolean => {
            return event.StreamTv && event.StreamTv.ID && decodeLiveStreamOperatorFromApi(event.StreamTv.Provider) != LiveStreamOperatorType.None
                && (
                    env.settings.liveStreamUA === -2
                    || env.settings.liveStreamUA === 0 /*&& env.loggedUserId > 0*/
                    || (env.settings.liveStreamUA > 0 && env.loggedUserId === env.settings.liveStreamUA));
        }

        const decodeLiveStreamOperatorFromApi = (operatorId: number): LiveStreamOperatorType => {
            switch (operatorId) {
                case 2: return LiveStreamOperatorType.BRVideo;
            }
            return LiveStreamOperatorType.None;
        };

        const setLiveData = (event: EventsItem, forceReadCompetitorsfromName: boolean) => {

            self.sportCategoryId = event.DisciplineID; //for tipos real discipline id (fixed), for MTS sport category id
            self.stateName = event.StateName ? event.StateName : ""; // null causes exception
            self.stateTime = event.StateTime;

            self.catIds = observable(event.CatIds);

            self.competitors = observable([]);

            /*if (__IsMTS__) {
                if (!event.ExternalID) {
                    event.ExternalID = "22439311";
                }
            }*/

            if (forceReadCompetitorsfromName) {
                getCompetitorNamesFromEventName(event.Name).forEach((c, ci) => {
                    self.competitors.push(LiveEventCompetitor.create(<SnapshotOut<typeof LiveEventCompetitor>>{
                        name: c, isServer: event.ActiveCompetitor == ci + 1
                    }))
                });
            } else {
                event.Competitors.forEach(competitor => {
                    self.competitors.push(LiveEventCompetitor.create(<SnapshotOut<typeof LiveEventCompetitor>>
                        {
                            name: competitor.Name,
                            isServer: competitor.IsServer //used for detail only in Tipos, IsServer comes (for list see ActiveCompetitor)
                        }
                    ))
                });
            }

            /*if (event.Results.length === 0) { //TODOHL uof data needs it now
                event.Results.push({ ID: 0, MainResult: true, Description: "", Flags: ResultFlag.None, IconID: 0, Name: "", ImportantResult: false, Score: null });
            }*/

            let results: Instance<typeof EventResultStore>[] = [];
            event.Results.forEach(result => {

                if ((result.Flags & ResultFlag.SuspendedPlayer) === ResultFlag.SuspendedPlayer) {
                    if (result.Score === "0:0") {
                        return;
                    }
                }

                const parsedScore = result.Score ? result.Score.split(':') : ['-', '-'];
                let resultStore = EventResultStore.create(<SnapshotOut<typeof EventResultStore>>{
                    id: result.ID,
                    //description: result.Description,
                    name: result.Name,
                    flags: result.Flags ?? 0,
                    iconId: __IsMTS__ ? result.ID : result.IconID,
                    importantResult: result.ImportantResult ?? false,
                    mainResult: result.MainResult ?? false,
                    score1: parsedScore[0],
                    score2: parsedScore[1] ? parsedScore[1] : '-'
                });
                resultStore.tailorScoreValues();
                results.push(resultStore);
            });
            self.results = observable(results);

            self.eventActions = observable(
                event.Actions
                    ? event.Actions.map(action => {
                        let iconCode = '0';
                        if (typeof action.Type === "number") {
                            if (action.Type === ActionType.Goal) {
                                iconCode = `${action.Type.toString()}-${env.settings.language.abbreviation}`;
                            }
                            else {
                                iconCode = action.Type.toString();
                            }
                        }

                        return EventActionStore.create(<SnapshotOut<typeof EventActionStore>>{
                            id: action.ID,
                            imageMapCode: iconCode,
                            matchTime: action.MatchTime == null ? null : secondsToMinutesForMatchTime(action.MatchTime),
                            name: action.Name,
                            sequenceId: action.SequenceId
                        })
                    }
                    )
                    : []
            );

            //inject start
            /*if (env.settings.liveStreamUA === -2) { //for debug, prod has -1 now
                event.StreamTv = { ID: "1", Provider: 2, StartDate: null };
            }*/

            /*TODOLS
            if (env.settings.liveStreamUA == 0) { //for debug, prod has -1 now
                if (eventData.GraphicsPresentationID != EventDisciplineMts.Football) {
                    eventData.StreamTv = { ID: "1", Provider: 2, StartDate: null };
                }
            }*/

            if (eventHasLiveStream(event)) {
                if (self.liveStream) {
                    self.liveStream.setData(decodeLiveStreamOperatorFromApi(event.StreamTv.Provider), event.StreamTv.ID, event.StreamTv.StartDate ? new Date(event.StreamTv.StartDate) : null);
                } else {
                    self.liveStream = LiveStreamInApi.create(<SnapshotOut<typeof LiveStreamInApi>>{
                        operator: decodeLiveStreamOperatorFromApi(event.StreamTv.Provider),
                        externalId: event.StreamTv.ID,
                        startDate: apiStringToDateConverter(event.StreamTv.StartDate).getTime()
                    });
                }
            }
            if (eventHasMatchTracker(event)) {
                if (self.matchTracker) {
                    if (event.MatchTracker) {
                        self.matchTracker.setData(event.MatchTracker.Type === undefined ? MatchTrackerType.BRMatch : event.MatchTracker.Type, event.MatchTracker.Id);
                    } else {
                        self.matchTracker.setData(MatchTrackerType.BRMatch, event.ExternalID);
                    }
                } else {
                    self.matchTracker = MatchTrackerInApi.create(<SnapshotOut<typeof MatchTrackerInApi>>{
                        id: event.MatchTracker ? event.MatchTracker.Id : event.ExternalID,
                        type: event.MatchTracker ? (event.MatchTracker.Type === undefined ? MatchTrackerType.BRMatch : event.MatchTracker.Type) : MatchTrackerType.BRMatch
                    });
                }
            } else {
                self.matchTracker = null;
            }
        }

        return {
            setLiveData,
            setBaseLiveData
        }
    });

export const LiveEventDetailStore = types.compose(
    BaseLiveEventStore,
    types.model({})
)
    .actions(self => {

        //const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        let oddBelowName = translation().live_OddBelowName;

        const setData = (eventData: EventsItem) => {
            self.setLiveData(eventData, __IsMTS__);

            let renameTipDataProvider = getRenameTipDataProviderFromCompetitors(self.competitors);

            self.setBaseLiveData(eventData, renameTipDataProvider, (odd: IOdds, game: IGame, gameDetail: IDetail, eventId: number, isEventSuspended: boolean, canConvertTipName: boolean, renameTipData: IRenameTipData) => {
                return createOddModel(
                    {
                        DetailID: gameDetail.ID,
                        EventID: eventId,
                        GameID: game.ID,
                        Name: __IsMTS__ ? odd.Name : `${odd.Name}${game.IsDetails && gameDetail.OddsList.length == 2 && gameDetail.OddsList[0].Name === oddBelowName /* ad hoc BelowOver */ ? ' ' + gameDetail.Name : ''}`,
                        PopularityPercentage: 0,
                        Rate: odd.Rate,
                        TipID: odd.TipID,
                        State: odd.State
                    },
                    isEventSuspended,
                    gameDetail.State,
                    canConvertTipName,
                    renameTipData
                );
            });
        }

        return {
            setData
        }
    });

export const MobileLiveEventListStore = types.compose(
    BaseLiveEventStore,
    types.model({})
)
    .views((self) => {
        return {
            get gameAndDetail() {
                if (self.gameGroups.length > 0 && self.gameGroups[0].games.length > 0 && self.gameGroups[0].games[0].details.length > 0) {
                    let game = self.gameGroups[0].games[0];
                    return { game: game, detail: game.details[0] };
                }
                return null;
            }
        }
    })
    .actions(self => {
        const setData = (eventData: EventsItem) => {
            eventData.GameGroups.forEach(gameGroup => { //show the first now
                gameGroup.Games = gameGroup.Games.length > 0 ? [gameGroup.Games[0]] : [];
            });

            self.setLiveData(eventData, true);

            let renameTipProvider = getRenameTipDataProviderFromCompetitors(self.competitors);

            self.setBaseLiveData(eventData, renameTipProvider, (odd: IOdds, game: IGame, gameDetail: IDetail, eventId: number, isEventSuspended: boolean, canConvertTipName: boolean, renameTipData: IRenameTipData) => {
                return createOddModel(
                    {
                        DetailID: gameDetail.ID,
                        EventID: eventId,
                        GameID: game.ID,
                        Name: odd.Name, //convert of below/under is not expected
                        PopularityPercentage: 0,
                        Rate: odd.Rate,
                        TipID: odd.TipID,
                        State: odd.State
                    },
                    isEventSuspended,
                    gameDetail.State,
                    canConvertTipName,
                    renameTipData
                );
            });
        }

        return {
            setData
        }
    });

export const DesktopLiveEventListStore = types.compose(  //MTS only
    BaseLiveEventStore,
    types.model({
        externalId: types.maybeNull(types.string),
        sequence: types.number,
        forceCanConvertTipName: types.optional(types.boolean, false) // for live in prematch (true), for desktop live false
    }))
    .views((self) => {
        return {
            get gameAndDetailForLeftMenu() {
                // it shows initial games (1x2, 12) only since there is no game name in UI
                //expecting that initial may be in first group has first game only (to avoid useless iterating of 3 groups)
                if (self.gameGroups.length > 0 && self.gameGroups[0].games.length > 0 && self.gameGroups[0].games[0].isInitial && self.gameGroups[0].games[0].details.length > 0) {
                    let game = self.gameGroups[0].games[0];
                    return { game: game, detail: game.details[0] };
                }
                return null;
            },
            gameAndDetailForGroup(groupId: string) { // uses the first game (expecting there is just one game)
                let group = self.gameGroups.find(g => g.id === groupId);
                if (group && group.games.length > 0 && group.games[0].details.length > 0) {
                    let game = group.games[0];
                    return { game: game, detail: game.details[0] };
                }
                return null;
            },
            gameAndDetailForGroupAndGame(groupId: string, rawGameId: number) { // uses game with rawGameId (markettype), for live in prematch
                let group = self.gameGroups.find(gg => gg.id === groupId);
                if (group) {
                    let game = group.games.find(g => g.id.startsWith(`${rawGameId}d`));
                    if (game && game.details.length) {
                        return { game: game, detail: game.details[0] };
                    }
                }
                return null;
            },
            canConvertTipName(): boolean {
                return self.forceCanConvertTipName;
            }
        }
    })
    .actions(self => {

        let env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        const setData = (eventData: EventsItem, graphicsPresentationId: number | null) => {
            //import all games (expected up to 3)

            self.setLiveData(eventData, true);
            self.externalId = eventData.ExternalID

            let renameTipDataProvider = getRenameTipDataProviderFromCompetitors(self.forceCanConvertTipName ? self.competitors : null);

            self.setBaseLiveData(eventData, renameTipDataProvider, (odd: IOdds, game: IGame, gameDetail: IDetail, eventId: number, isEventSuspended: boolean, canConvertTipName: boolean, renameTipData: IRenameTipData) => {
                return createOddModel(
                    {
                        DetailID: gameDetail.ID,
                        EventID: eventId,
                        GameID: game.ID,
                        Name: odd.Name, //convert of below/under is not expected
                        PopularityPercentage: 0,
                        Rate: odd.Rate,
                        TipID: odd.TipID,
                        State: odd.State
                    },
                    isEventSuspended,
                    gameDetail.State,
                    canConvertTipName,
                    renameTipData
                );
            });
            self.graphicsPresentationId = graphicsPresentationId;

            if (env.ticketRateSyncService) {
                processDesktopLiveRateChanges(env.ticketRateSyncService, eventData);
            }
        }

        return {
            setData
        }
    });

export type ILiveTVEventListStore = Instance<typeof LiveTVEventListStore>;

export const LiveTVEventListStore = types.compose(
    MobileLiveEventListStore,
    types.model({}))
    .views((_self) => {
        return {
            canConvertTipName(_game: IGame): boolean {
                return false; //override of parent
            }
        }
    })
    .actions(self => {

        const setData = (eventData: EventsItem, graphicsPresentationId: number) => {
            //import all games (expected up to 3)

            self.setLiveData(eventData, true);

            let renameTipDataProvider = getRenameTipDataProviderFromCompetitors(null);

            self.setBaseLiveData(eventData, renameTipDataProvider, (odd: IOdds, game: IGame, gameDetail: IDetail, eventId: number, isEventSuspended: boolean, canConvertTipName: boolean, renameTipData: IRenameTipData) => {
                return createOddModel(
                    {
                        DetailID: gameDetail.ID,
                        EventID: eventId,
                        GameID: game.ID,
                        Name: odd.Name,
                        PopularityPercentage: 0,
                        Rate: odd.Rate/* + 10 TODO to test higher rates */,
                        TipID: odd.TipID,
                        State: odd.State
                    },
                    isEventSuspended,
                    gameDetail.State,
                    canConvertTipName,
                    renameTipData
                );
            });

            // under/over games:  underXX overXX => under XX over
            self.gameGroups.forEach(gg => gg.games.forEach(g => {
                const gameOdds = g.details[0].odds;

                if (gameOdds.length === 2) {
                    const parsed = parseUnderOver(gameOdds);
                    if (parsed.hasDetail) {
                        gameOdds[0].name = parsed.under;
                        gameOdds[1].name = parsed.over;
                        g.details[0].name = parsed.detail;
                    }
                }
            }))

            self.graphicsPresentationId = graphicsPresentationId; //fix missing graphicsPresentationId on event
        }

        return {
            setData,
        }
    });

export type ILiveTVEventDetailStore = Instance<typeof LiveTVEventDetailStore>;

export const LiveTVEventDetailStore = types.compose(
    LiveEventDetailStore,
    types.model({
        hasData: types.maybeNull(types.boolean)
    }))
    .views((self) => {
        return {
            get allGames(): SnapshotOut<typeof gameStore>[] {
                let ret: SnapshotOut<typeof gameStore>[] = [];
                if (self.gameGroups) {
                    self.gameGroups.forEach(gamegroup => { gamegroup.games.forEach(game => { ret.push(game) }) });
                }
                return ret;
            },
            canConvertTipName(_game: IGame): boolean {
                return false; //override of parent
            }
        }
    })
    .actions(self => {

        const setData = (eventData: EventsItem) => {
            self.setLiveData(eventData, true);

            let renameTipDataProvider = getRenameTipDataProviderFromCompetitors(null);

            self.setBaseLiveData(eventData, renameTipDataProvider, (odd: IOdds, game: IGame, gameDetail: IDetail, eventId: number, isEventSuspended: boolean, canConvertTipName: boolean, renameTipData: IRenameTipData) => {
                return createOddModel(
                    {
                        DetailID: gameDetail.ID,
                        EventID: eventId,
                        GameID: game.ID,
                        Name: odd.Name,
                        PopularityPercentage: 0,
                        Rate: odd.Rate,
                        TipID: odd.TipID,
                        State: odd.State
                    },
                    isEventSuspended,
                    gameDetail.State,
                    canConvertTipName,
                    renameTipData
                );
            });

            //self.gameGroups.forEach(gg => gg.games.forEach(g => { g.details.forEach(d => d.odds.forEach(o => o.name = 'pod (12.5)')) }))
            // under/over games:  underXX overXX => under XX over
            self.gameGroups.forEach(gg => gg.games.forEach(g => {
                const gameOdds = g.details[0].odds;

                if (gameOdds.length === 2) {
                    const parsed = parseUnderOver(gameOdds);
                    if (parsed.hasDetail) {
                        gameOdds[0].name = parsed.under;
                        gameOdds[1].name = parsed.over;
                        g.details[0].name = parsed.detail;
                    }
                }
            }))

            self.hasData = true;
        }

        return {
            setData
        }
    });

interface IParsedUnderOver {
    detail: string;
    under: string;
    over: string;
    hasDetail: boolean;
}

const parseUnderOver = (odds: Instance<typeof OddModel>[]): IParsedUnderOver => {
    let detail = '';
    let regEx = /[(][0-9]+[.,][0-9]+[)]/gi;

    const d1 = odds[0].name.match(regEx);
    const d2 = odds[1].name.match(regEx);

    if (d1 && d2 && d1[0] === d2[0]) {
        detail = d1[0].match(/[0-9]+[.,][0-9]+/gi)[0];
    }

    return {
        hasDetail: detail !== '',
        detail: detail,
        under: odds[0].name.replace(regEx, '').trim(),
        over: odds[1].name.replace(regEx, '').trim()
    }
}

export type IDesktopLiveEventListStore = Instance<typeof DesktopLiveEventListStore>;

export const DesktopLiveEventDetailStore = types.compose(
    LiveEventDetailStore,
    types.model({}))
    .actions(self => {

        let env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        const parentSetData = self.setData;
        const setData = (eventData: EventsItem): void => {
            parentSetData(eventData);

            if (env.ticketRateSyncService) {
                processDesktopLiveRateChanges(env.ticketRateSyncService, eventData);
            }
        }

        return {
            setData
        }
    });

const processDesktopLiveRateChanges = (ticketRateSyncService: ITicketRateSyncService, eventData: EventsItem): void => {
    if (ticketRateSyncService && eventData.GameGroups) {
        let rows = ticketRateSyncService.getRows();
        let changes: { id: string, rate: number }[] = null;
        eventData.GameGroups.forEach(gg => gg.Games.forEach(g => g.Details.forEach(d => d.OddsList.forEach(o => {
            if (rows.has(o.TipID) && o.Rate !== rows.get(o.TipID)) {
                if (!changes) {
                    changes = [];
                }
                changes.push({ id: o.TipID, rate: o.Rate });
            }
        }))));
        if (changes) {
            ticketRateSyncService.ratesChanged(changes);
        }
    }
}